import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Select,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  AppleEnhancedDesignFeaturedActions,
  FeaturedAction,
  FeaturedActionSlotKey,
  FeaturedActionType,
} from '../../../store/types/template';
import RemoveButton from './RemoveButton';
import DragDotsIcon from '../../../theme/icons/DragDotsIcon';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import StrictModeDroppable from './utils/StrictModeDroppable';
import {
  FEATURED_ACTION_LABELS,
  FEATURED_ACTION_SLOT_KEYS,
  FEATURED_ACTION_TYPES,
  isFeaturedActionSlotFilled,
} from './utils/featuredActionTypes';

const SlotError = ({ message }: { message?: string }) =>
  message ? (
    <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
      <CustomErrorIcon />
      {message}
    </FormErrorMessage>
  ) : null;

const otherSlot = (slot: FeaturedActionSlotKey): FeaturedActionSlotKey =>
  slot === 'one' ? 'two' : 'one';

interface FeaturedActionSlotsProps {
  value: AppleEnhancedDesignFeaturedActions;
  errors: Record<string, string | undefined>;
  errorPrefix: string;
  onChange: (
    slot: FeaturedActionSlotKey,
    partial: Partial<FeaturedAction>
  ) => void;
  onRemove: (slot: FeaturedActionSlotKey) => void;
  onSwap: () => void;
  onFieldFocus?: (path: string) => void;
  onFieldBlur?: () => void;
}

const FeaturedActionSlots = ({
  value,
  errors,
  errorPrefix,
  onChange,
  onRemove,
  onSwap,
  onFieldFocus,
  onFieldBlur,
}: FeaturedActionSlotsProps) => {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    onSwap();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <StrictModeDroppable droppableId="featured-actions">
        {(provided) => (
          <Box ref={provided.innerRef} {...provided.droppableProps}>
            {FEATURED_ACTION_SLOT_KEYS.map((slot, index) => {
              const action = value[slot];
              const sibling = value[otherSlot(slot)];
              const filled = isFeaturedActionSlotFilled(action);
              const typeError = errors[`${errorPrefix}.${slot}.type`];
              const urlError = errors[`${errorPrefix}.${slot}.url`];

              return (
                <Box key={slot}>
                  {index > 0 && <Divider borderColor="lightGrey" my={4} />}
                  <Draggable
                    draggableId={`featured-action-${slot}`}
                    index={index}
                  >
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                      >
                        <Flex align="flex-start">
                          <Flex
                            {...dragProvided.dragHandleProps}
                            cursor="grab"
                            flexShrink={0}
                            align="center"
                            gap={3}
                            mr={4}
                            ml={-2}
                            h="90px"
                            mt={5}
                          >
                            <DragDotsIcon w="7px" h="13px" color="#989898" />
                            <Box h="100%" w="1px" bg="#989898" mt="5px" />
                          </Flex>
                          <Box flex={1}>
                            <FormControl isInvalid={!!typeError} mb={3}>
                              <Select
                                value={action.type ?? ''}
                                placeholder="Select an action"
                                onFocus={() => onFieldFocus?.(`${slot}.type`)}
                                onBlur={() => onFieldBlur?.()}
                                onChange={(e) =>
                                  onChange(slot, {
                                    type: (e.target.value ||
                                      null) as FeaturedActionType | null,
                                  })
                                }
                              >
                                {FEATURED_ACTION_TYPES.map((type) => (
                                  <option
                                    key={type}
                                    value={type}
                                    disabled={sibling.type === type}
                                  >
                                    {FEATURED_ACTION_LABELS[type]}
                                  </option>
                                ))}
                              </Select>
                              <SlotError message={typeError} />
                            </FormControl>
                            <FormControl
                              isInvalid={!!urlError}
                              mb={filled ? 3 : 0}
                            >
                              <Input
                                value={action.url}
                                placeholder="https://www.passentry.com"
                                onFocus={() => onFieldFocus?.(`${slot}.url`)}
                                onBlur={() => onFieldBlur?.()}
                                onChange={(e) =>
                                  onChange(slot, { url: e.target.value })
                                }
                              />
                              <SlotError message={urlError} />
                            </FormControl>
                            {filled && (
                              <RemoveButton
                                label="Remove"
                                onClick={() => onRemove(slot)}
                              />
                            )}
                          </Box>
                        </Flex>
                      </Box>
                    )}
                  </Draggable>
                </Box>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default FeaturedActionSlots;
